import { useCallback, useEffect, useMemo, useState } from "react";
import { useTableFilters } from "../../../components/data-table";
import useSessionState from "../../../hooks/useSessionState";
import { fetchAllReservationsForTab, filterReservations, RESERVATION_FILTER_KEYS } from "../list";

/**
 * Data, filters and paging for one reservation tab. The desktop table and the mobile card list
 * both render from this, so they search, filter and reset the same way and share one fetch.
 * Filters are remembered per tab for the session, so coming back from a reservation keeps them.
 */
export default function useReservationList(tabKey) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [storedPage, setCurrentPage] = useSessionState(`reservations:${tabKey}:page`, 1);
    const [pageSize, setPageSize] = useSessionState("reservations:pageSize", 10);

    const table = useTableFilters({
        persistKey: `reservations:${tabKey}`,
        onChange: () => setCurrentPage(1),
    });
    const assignment = table.filters[RESERVATION_FILTER_KEYS.ASSIGNMENT] ?? "all";

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            setRows(await fetchAllReservationsForTab({ tabKey, upcomingFilter: assignment }));
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [tabKey, assignment]);

    useEffect(() => {
        reload();
    }, [reload]);

    const filteredRows = useMemo(() => filterReservations(rows, table.filters), [rows, table.filters]);
    // A remembered page can be past the end once rows disappear (e.g. the last one was ended).
    const lastPage = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    const currentPage = loading ? storedPage : Math.min(storedPage, lastPage);
    const pageRows = useMemo(
        () => filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        [filteredRows, currentPage, pageSize],
    );

    return {
        tabKey,
        loading,
        table,
        assignment,
        filteredRows,
        pageRows,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        reload,
    };
}
